import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Search, Plus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { isISBNQuery, normalizeISBN } from "@/utils/isbnUtils";

interface SearchAwardBookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBookSelected?: (book: any) => void;
}

export function SearchAwardBookDialog({
  open,
  onOpenChange,
  onBookSelected,
}: SearchAwardBookDialogProps) {
  const [searchInput, setSearchInput] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<any>(null);

  const handleSearch = async () => {
    const userInput = searchInput.trim();
    
    if (!userInput) {
      toast.error("Please enter a book title or ISBN");
      return;
    }

    // Detect if user input is an ISBN
    const searchIsbn = isISBNQuery(userInput) ? normalizeISBN(userInput) : undefined;
    const searchTitle = !searchIsbn ? userInput : undefined;

    setSearching(true);
    try {
      const { data, error } = await supabase.functions.invoke('search-and-enrich-series-book', {
        body: {
          isbn: searchIsbn,
          bookTitle: searchTitle,
        },
      });

      if (error) throw error;

      setResults(data);
      
      if (data.existingBooks.length === 0 && data.openLibraryResults.length === 0) {
        toast.info("No results found. Try a different search.");
      }
    } catch (error: any) {
      console.error('Search error:', error);
      toast.error(`Search failed: ${error.message}`);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectBook = (book: any, source: 'existing' | 'openlibrary') => {
    if (source === 'existing') {
      onBookSelected?.(book);
      toast.success("Book selected from library");
      onOpenChange(false);
      setSearchInput("");
      setResults(null);
    } else {
      handleImportFromAPI(book);
    }
  };

  const handleImportFromAPI = async (book: any) => {
    try {
      const { data: newBook, error } = await supabase
        .from('books')
        .insert({
          title: book.title,
          authors: book.authors,
          cover_url: book.cover_url,
          isbn13: book.isbn13,
          published_year: book.publishedYear,
          publisher: book.publisher,
          page_count: book.page_count,
          synopsis: book.synopsis,
          source: book.source || 'api_import',
        })
        .select()
        .single();

      if (error) throw error;

      onBookSelected?.(newBook);
      toast.success("Book imported and selected");
      onOpenChange(false);
      setSearchInput("");
      setResults(null);
    } catch (error: any) {
      console.error('Import error:', error);
      toast.error(`Failed to import book: ${error.message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => {
      onOpenChange(open);
      if (!open) {
        setSearchInput("");
        setResults(null);
      }
    }}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Search & Add Book to Award</DialogTitle>
          <DialogDescription>
            Search by book title or ISBN to quickly find and add books
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1 space-y-2">
              <Label htmlFor="searchInput">Book Title or ISBN</Label>
              <Input
                id="searchInput"
                placeholder="Enter book title or ISBN (e.g., 978-0143107149)..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button
              onClick={handleSearch}
              disabled={searching || !searchInput.trim()}
              className="mt-8"
            >
              {searching ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="mr-2 h-4 w-4" />
                  Search
                </>
              )}
            </Button>
          </div>

          {results && (
            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
              {results.existingBooks.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">In Your Library</h3>
                  <div className="space-y-2">
                    {results.existingBooks.map((book: any) => (
                      <Card key={book.id} className="p-3 bg-card">
                        <div className="flex gap-3 items-start">
                          {book.cover_url && (
                            <img
                              src={book.cover_url}
                              alt={book.title}
                              className="w-12 h-16 object-cover rounded flex-shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <h4 className="font-medium break-words line-clamp-2">{book.title}</h4>
                            <p className="text-sm text-muted-foreground truncate">
                              {book.authors?.join(', ')}
                            </p>
                            {book.isbn13 && (
                              <p className="text-xs text-muted-foreground">
                                ISBN: {book.isbn13}
                              </p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleSelectBook(book, 'existing')}
                            className="flex-shrink-0 whitespace-nowrap"
                          >
                            <Plus className="mr-1 h-3 w-3" />
                            Select
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {results.openLibraryResults.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">From Book APIs</h3>
                  <div className="space-y-2">
                    {results.openLibraryResults.map((book: any, idx: number) => (
                      <Card key={idx} className="p-3 bg-card">
                        <div className="flex gap-3 items-start">
                          {book.cover_url && (
                            <img
                              src={book.cover_url}
                              alt={book.title}
                              className="w-12 h-16 object-cover rounded flex-shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <h4 className="font-medium break-words line-clamp-2">{book.title}</h4>
                            <p className="text-sm text-muted-foreground truncate">
                              {book.authors?.join(', ')}
                            </p>
                            <div className="flex gap-2 text-xs text-muted-foreground mt-1">
                              {book.publishedYear && (
                                <span>Published: {book.publishedYear}</span>
                              )}
                              {book.isbn13 && (
                                <span>• ISBN: {book.isbn13}</span>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleSelectBook(book, 'openlibrary')}
                            className="flex-shrink-0 whitespace-nowrap"
                          >
                            <Plus className="mr-1 h-3 w-3" />
                            Import
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {results.existingBooks.length === 0 && results.openLibraryResults.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No results found. Try adjusting your search or check the ISBN.
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}